import { withDevtools } from '@angular-architects/ngrx-toolkit';
import { computed, inject, Signal } from '@angular/core';
import { Router } from '@angular/router';
import { CreateLicenseOfferRequest, LicenseOffer, UpdateLicenseOfferRequest } from '@models/csp';
import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals';
import { addEntity, createCustomStateSetOnUrlHandler, deleteEntity, getAll, StateDictionary, updateEntity } from '../../helpers';
import { LicenseOffersService } from './license-offers.service';

type LicenseOffersState = {
  licenseOffers: LicenseOffer[];
  selectedLicenseOfferId: string | null;
  isLoading: boolean;
  licenseOfferCreateLoading: boolean;
};

const initialState: LicenseOffersState = {
  licenseOffers: [],
  selectedLicenseOfferId: null,
  isLoading: false,
  licenseOfferCreateLoading: false,
};

const licenseOffersDictionary: StateDictionary<LicenseOffer> = {
  entityList: 'licenseOffers',
  listLoading: 'isLoading',
  createLoading: 'licenseOfferCreateLoading',
  selectedEntity: '',
  selectedEntityLoading: '',
  entityId: 'externalId',
  sortFn: (a: LicenseOffer, b: LicenseOffer) => a.externalId.localeCompare(b.externalId),
};

export const LicenseOffersStore = signalStore(
  withState(initialState),
  withDevtools('license-offers-store'),
  withComputed(state => ({
    selectedLicenseOffer: computed(
      () => state.licenseOffers().find(licenseOffer => licenseOffer.externalId === state.selectedLicenseOfferId()) || null,
    ),
  })),
  withMethods((store, licenseOffersService = inject(LicenseOffersService), router = inject(Router)) => {
    createCustomStateSetOnUrlHandler<LicenseOffersState>('/csp/offers', { selectedLicenseOfferId: null })(store);

    const repositoryMethods = {
      getAll: getAll<LicenseOffer>({
        store,
        service: licenseOffersService,
        dictionary: licenseOffersDictionary,
      }),

      addLicenseOffer: addEntity<LicenseOffer, CreateLicenseOfferRequest>({
        store,
        service: licenseOffersService,
        dictionary: licenseOffersDictionary,
        onSuccessCallback: (newLicenseOffer: LicenseOffer) => {
          patchState(store, { selectedLicenseOfferId: newLicenseOffer.externalId });
          router.navigate(['csp', 'offers', newLicenseOffer.externalId, 'details']);
        },
      }),

      updateLicenseOffer: updateEntity<LicenseOffer, UpdateLicenseOfferRequest>({
        store,
        service: licenseOffersService,
        dictionary: licenseOffersDictionary,
        onSuccessCallback: (updatedLicenseOffer: LicenseOffer) => {
          patchState(store, { selectedLicenseOfferId: updatedLicenseOffer.externalId });
        },
      }),

      deleteLicenseOffer: deleteEntity<LicenseOffer>({
        store,
        service: licenseOffersService,
        dictionary: licenseOffersDictionary,
        onSuccessCallback: () => {
          patchState(store, {
            selectedLicenseOfferId: null,
          });
          router.navigate(['csp', 'offers']);
        },
      }),
    };

    const navigationActions = {
      selectLicenseOffer: (id: string): void => {
        if (!store.licenseOffers().length && !store.isLoading()) repositoryMethods.getAll();

        patchState(store, { selectedLicenseOfferId: id });
      },

      goToLicenseOfferActions: async (id: string): Promise<boolean> => {
        const result = await router.navigate(['csp', 'offers', id]);
        if (!result) return false;
        patchState(store, { selectedLicenseOfferId: id });
        return true;
      },

      goToListPage: (): void => {
        patchState(store, {
          selectedLicenseOfferId: null,
        });
        router.navigate(['csp', 'offers']);
      },

      resetState: (state: Partial<LicenseOffersState>): void => patchState(store, state),
    };

    return {
      ...repositoryMethods,
      ...navigationActions,
    };
  }),
);

export interface LicenseOffersStoreType {
  readonly licenseOffers: Signal<LicenseOffer[]>;
  readonly selectedLicenseOfferId: Signal<string | null>;
  readonly isLoading: Signal<boolean>;
  readonly licenseOfferCreateLoading: Signal<boolean>;

  readonly selectedLicenseOffer: Signal<LicenseOffer | null>;

  readonly getAll: () => void;
  readonly addLicenseOffer: (request: CreateLicenseOfferRequest) => void;
  readonly updateLicenseOffer: (input: { id: string; dto: UpdateLicenseOfferRequest }) => void;
  readonly deleteLicenseOffer: (id: string) => void;
  readonly selectLicenseOffer: (id: string) => void;
  readonly goToLicenseOfferActions: (id: string) => Promise<boolean>;
  readonly goToListPage: () => void;
  readonly resetState: (state: Partial<LicenseOffersState>) => void;
}
